import React, { useEffect, useMemo, useState, type FunctionComponent } from "react";
import { ClockCircleOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Alert,
  Badge,
  Button,
  Card,
  Divider,
  Empty,
  Image,
  Input,
  List,
  Popconfirm,
  Skeleton,
  Space,
  Tree,
} from "antd";
import { type DataNode, type EventDataNode } from "antd/lib/tree";
import dayjs from "dayjs";
import groupBy from "lodash.groupby";
import keyBy from "lodash.keyby";
import sortBy from "lodash.sortby";
import truncate from "lodash.truncate";
import uniq from "lodash.uniq";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { productResourcesActions, productResourcesSelectors, RequestStatus } from "@fitness-app/app-store";
import {
  ProductResourceStatus,
  type InAppResource,
  type ProductResource,
} from "@fitness-app/data-models/entities/ProductResource";
import { DEFAULT_RESOURCE_CATEGORY } from "@fitness-app/utils/src/constants/defaultResourceCategory";

import IconText from "~/components/IconText/IconText";
import ResourceContent from "~/components/ResourceContent/ResourceContent";
import { useAppDispatch, useAppSelector } from "~/store/initializeStore";

interface OwnProps {
  ownerId?: string;
  readonly?: boolean;
  productId: string;
}

type Props = OwnProps;

export const statusMapper = {
  [ProductResourceStatus.AVAILABLE]: "success",
  [ProductResourceStatus.DRAFT]: "processing",
  // Warning, like UNAVAILABLE: both mean "the material is there but the clients are not seeing
  // it" — IN_REVIEW only reaches the flagged test group.
  [ProductResourceStatus.IN_REVIEW]: "warning",
  [ProductResourceStatus.UNAVAILABLE]: "warning",
} as const;

const ITEM_SEPARATOR = "__";
const { DirectoryTree } = Tree;

const ResourcesList: FunctionComponent<Props> = ({ readonly, productId }) => {
  const { t } = useTranslation("products");
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [selectedResource, setResource] = useState<null | ProductResource>(null);
  const [isSticky, setSticky] = useState(false);
  const [selectedCategory, setCategory] = useState(DEFAULT_RESOURCE_CATEGORY);
  const [selectedKeys, onKeysChange] = useState<React.Key[]>([DEFAULT_RESOURCE_CATEGORY]);
  const [expandedKeys, changeExpandedKeys] = useState<React.Key[]>([DEFAULT_RESOURCE_CATEGORY]);
  const [searchValue, setSearchValue] = useState("");
  const resources = useAppSelector(productResourcesSelectors.getFilteredResources);
  const { error, data, status, searchTerm, categories } = useAppSelector((store) => store.productResources);
  const user = useAppSelector((store) => store.user);

  const fetchInitialPage = () => {
    void dispatch(productResourcesActions.fetchProductResources({ productId }));
  };

  const handleResourceRemove = (item: ProductResource) => {
    void dispatch(
      productResourcesActions.removeProductResource({
        productId,
        resourceId: item.id,
      }),
    );
  };

  const onSelect = (keys: React.Key[], { node }: { node: EventDataNode<DataNode> }) => {
    onKeysChange(keys);
    if (node.isLeaf && typeof node.key === "string") {
      const [category, id] = node.key.split(ITEM_SEPARATOR);
      setCategory(category || DEFAULT_RESOURCE_CATEGORY);
      const selected = data.find((item) => item.id === id);
      if (selected) {
        setResource(selected);
        changeExpandedKeys((prev) => uniq([...prev, category]) as React.Key[]);
      }
    } else {
      setResource(null);
      setCategory(node.key as string);
    }
  };

  const handleScroll = () => {
    if (window.scrollY > 200 && !isSticky) {
      setSticky(true);
    } else {
      setSticky(false);
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", () => handleScroll);
    };
  }, []);

  const onExpand = (keys: React.Key[]) => {
    changeExpandedKeys(keys);
  };

  const resourcesByGroup = useMemo(() => {
    const categoriesByKey = keyBy(categories, "id");
    const byCategory = groupBy(
      resources.map((item) => ({
        ...item,
        category: item.category ? categoriesByKey[item.category]?.name || null : null,
      })),
      "category",
    );
    changeExpandedKeys(Object.keys(byCategory));
    return byCategory;
  }, [resources, categories]);

  useEffect(() => {
    const firstCategoryOnList = Object.keys(resourcesByGroup)[0];
    if (
      selectedCategory === DEFAULT_RESOURCE_CATEGORY &&
      !resourcesByGroup[DEFAULT_RESOURCE_CATEGORY] &&
      firstCategoryOnList
    ) {
      const firstCategoryOnList = Object.keys(resourcesByGroup)[0];
      setCategory(firstCategoryOnList || DEFAULT_RESOURCE_CATEGORY);
      onKeysChange(firstCategoryOnList ? [firstCategoryOnList] : []);
    }
  }, [resourcesByGroup, selectedCategory]);

  const treeData = useMemo(() => {
    return sortBy(
      Object.entries(resourcesByGroup).map(([categoryName, resources]) => ({
        title:
          categoryName === DEFAULT_RESOURCE_CATEGORY || categoryName === "null"
            ? t("productResources.form.general")
            : categoryName,
        key: categoryName,
        children: resources.map((resource) => ({
          title: truncate(resource.title, { length: 25 }),
          key: `${categoryName}${ITEM_SEPARATOR}${resource.id}`,
          isLeaf: true,
        })),
      })),
      (category) => category.title !== t("productResources.form.general"),
    );
  }, [resourcesByGroup, resources]);

  const onItemSelect = (resource: InAppResource) => {
    setResource(resource);
    if (resource.category) {
      onKeysChange([`${resource.category}${ITEM_SEPARATOR}${resource.id}`]);
      changeExpandedKeys((prev) => uniq([...prev, resource.category ?? ""]));
    }
  };

  const goBack = (resource: InAppResource) => {
    setResource(null);
    if (resource.category) {
      setCategory(resource.category);
      onKeysChange([resource.category]);
    }
  };

  const onClear = () => {
    setSearchValue("");
    dispatch(productResourcesActions.clearSearchMode());
  };

  const onSearch = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) {
      if (searchTerm) {
        onClear();
      }
      return;
    }
    dispatch(productResourcesActions.setSearchTerm(trimmed));
  };

  return (
    <Card
      className="relative min-h-screen"
      title={t("productResources.list")}
      extra={
        <Button icon={<PlusOutlined />} type="primary" onClick={() => navigate(`${pathname}/add`)} disabled={readonly}>
          {t("productResources.addResource")}
        </Button>
      }
    >
      <Space size={4}>
        <Input.Search
          placeholder={t<string>("resourcesSearch.placeholder")}
          onSearch={onSearch}
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          style={{ width: 300, margin: "20px 0 40px 0" }}
        />
        {Boolean(searchTerm) && (
          <Button onClick={onClear} type="link" style={{ margin: "20px 0 40px 0" }}>
            {t("resourcesSearch.reset")}
          </Button>
        )}
      </Space>
      <div className="flex">
        {treeData?.length ? (
          <div className="pr-4.5 absolute flex h-screen max-w-[330px]">
            <div
              className="relative z-10 w-[300px] overflow-auto"
              style={isSticky ? { top: 5, position: "fixed" } : undefined}
            >
              <DirectoryTree
                defaultExpandAll
                onSelect={onSelect}
                treeData={treeData}
                expandedKeys={expandedKeys}
                onExpand={onExpand}
                selectedKeys={selectedKeys}
              />
            </div>
          </div>
        ) : null}
        <div className="ml-[330px] flex min-h-screen w-full" style={!treeData?.length ? { marginLeft: 0 } : undefined}>
          {treeData?.length ? <Divider type="vertical" className="border-l-0.5 h-full px-3 py-0" /> : null}
          <div className="h-full w-full">
            {selectedResource && user.data ? (
              <ResourceContent
                withTitle
                resource={selectedResource}
                author={{
                  email: user.data.email ?? "",
                  firstName: user.profile?.firstName ?? "",
                  lastName: user.profile?.lastName ?? "",
                  avatar: user.profile?.avatarUrl ?? null,
                }}
                goBack={goBack}
              />
            ) : (
              <List<ProductResource>
                loading={status === RequestStatus.SUBSCRIBING}
                bordered={false}
                locale={{
                  emptyText: error ? (
                    <Space direction="vertical" style={{ width: "100%" }} size={24}>
                      <Alert showIcon type="error" message={error} />
                      <Button onClick={fetchInitialPage}>{t("button.refresh")}</Button>
                    </Space>
                  ) : (
                    <Empty
                      description={searchValue ? t<string>("resourcesSearch.empty") : t("productResources.empty")}
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  ),
                }}
                dataSource={resourcesByGroup[selectedCategory] ?? []}
                renderItem={(item) => (
                  <List.Item
                    actions={[
                      <Button type="link" onClick={() => onItemSelect(item)} key="edit-link">
                        {t("button.see")}
                      </Button>,
                      <Button
                        type="link"
                        onClick={() => navigate(`${pathname}/${item.id}`)}
                        key="edit-link"
                        disabled={readonly}
                      >
                        {t("button.edit")}
                      </Button>,
                      <Popconfirm
                        key="remove-link"
                        overlayStyle={{ maxWidth: 400 }}
                        placement="leftTop"
                        title={t("productResources.deleteWarning")}
                        onConfirm={() => handleResourceRemove(item)}
                      >
                        <Button type="link" danger disabled={readonly}>
                          {t("button.delete")}
                        </Button>
                      </Popconfirm>,
                    ]}
                  >
                    <Skeleton avatar title={false} loading={false} active>
                      <List.Item.Meta
                        style={{
                          alignItems: "center",
                        }}
                        avatar={
                          <Image
                            width={80}
                            height={80}
                            style={{
                              objectFit: "cover",
                            }}
                            src={Array.isArray(item.images) && item.images.length ? item.images[0] : "error"}
                            fallback="data:image/png;base64,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"
                          />
                        }
                        title={readonly ? item.title : <Link to={`${pathname}/${item.id}`}>{item.title}</Link>}
                        description={
                          <Space size={16}>
                            <IconText
                              icon={ClockCircleOutlined as FunctionComponent<unknown>}
                              text={dayjs(item.createdAt).format("HH:mm DD.MM.YYYY")}
                              key="item-time"
                            />
                            <Badge
                              status={statusMapper[item.status ?? ProductResourceStatus.DRAFT]}
                              text={t(
                                `common:productResourceStatus.${(
                                  item.status || ProductResourceStatus.DRAFT
                                ).toLowerCase()}`,
                              )}
                            />
                          </Space>
                        }
                      />
                    </Skeleton>
                  </List.Item>
                )}
              />
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

export default ResourcesList;
